import { HttpErrorResponse, HttpEventType, HttpStatusCode } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ClinicalFeedbackConstants } from '@app/clinical-feedback/clinical-feedback-constants';
import { acceptedFiletypes, FormElementModel } from '@app/clinical-feedback/clinical-feedback.model';
import { ClinicalFeedbackService } from '@app/clinical-feedback/clinical-feedback.service';
import { NewClinicalFeedbackFormHelper } from '@app/clinical-feedback/new-clinical-feedback/new-clinical-feedback.helper';
import { CommonUtilities } from '@app/shared/common-utilities';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ClinicalFeedbackFile } from './clinical-feedback-file.model';

@Component({
    selector: 'app-file-upload',
    styleUrls: ['./file-upload.component.scss'],
    templateUrl: './file-upload.component.html',
    encapsulation: ViewEncapsulation.None
})
export class FileUploadComponent implements OnInit, OnDestroy {
    @Input() accessionNumber!: string;
    @Input() parentFormGroup!: UntypedFormGroup;
    @Input() formElementModel!: FormElementModel;
    acceptedFileExtensions = '';
    maxUploadSize = CommonUtilities.bytesToMegabytes(ClinicalFeedbackConstants.MAX_SINGLE_FILE_UPLOAD_SIZE);
    maxFileAmount = ClinicalFeedbackConstants.MAX_FILE_AMOUNT;

    files: ClinicalFeedbackFile[] = [];
    private readonly subscription: Subscription = new Subscription();

    constructor(private readonly clinicalFeedbackService: ClinicalFeedbackService) {}

    ngOnInit(): void {
        this.acceptedFileExtensions = acceptedFiletypes.map(f => f.value).join(', ');
    }

    get control() {
        if (!this.formElementModel.id) {
            throw new Error(`formElementId not set for '${this.formElementModel.label}'.`);
        }

        return this.parentFormGroup.get(this.formElementModel.id);
    }

    get asterisk() {
        return NewClinicalFeedbackFormHelper.getAsteriskIfRequired(this.control);
    }

    onFileSelected(event: Event) {
        const eventTarget = event.target as HTMLInputElement;
        Array.from(eventTarget.files).forEach(file => this.uploadFiles(file));
        //resets the value of file input so the same file can be uploaded again (onChange wouldn't fire for same file)
        const fileInput = event.target as HTMLInputElement;
        fileInput.value = '';
    }

    onFileDropped(files: FileList) {
        Array.from(files).forEach(file => this.uploadFiles(file));
    }

    handleFileRemoval(key: string) {
        const file = this.files.find(f => f.id === key);
        if (file && !file.success) {
            this.cancelUpload(file);
        }
        this.subscription.add(this.clinicalFeedbackService.deleteAttachment(this.accessionNumber, key).subscribe());
        this.removeFileFromListAndFormControl(key);
    }

    private cancelUpload(file: ClinicalFeedbackFile) {
        file.subscription.unsubscribe();
        file.uploadProgress = 0;
    }

    private removeFileFromListAndFormControl(key: string) {
        this.files = this.files.filter(f => f.id !== key);
        this.removeFileFromFormControl(key);
    }

    private uploadFiles(uploadedFile: File) {
        if (uploadedFile) {
            const clinicalFeedbackFile = this.addFileToDisplayList(uploadedFile);
            clinicalFeedbackFile.uploading = true;
            if (this.validateFile(uploadedFile, clinicalFeedbackFile)) {
                const formData = new FormData();
                formData.append('Document', uploadedFile);
                formData.append('AccessionNumber', this.accessionNumber);
                formData.append('DocumentId', clinicalFeedbackFile.id);

                const uploadSubscription = this.clinicalFeedbackService.uploadAttachment(formData).subscribe({
                    next: event => {
                        if (event.type === HttpEventType.UploadProgress && event.total) {
                            clinicalFeedbackFile.uploadProgress = Math.round(
                                // eslint-disable-next-line no-magic-numbers
                                100 * (event.loaded / event.total)
                            );
                            if (event.loaded === event.total) {
                                clinicalFeedbackFile.progressBarMode = 'indeterminate';
                            }
                        } else if (event.type === HttpEventType.Response && event.status === HttpStatusCode.Ok) {
                            clinicalFeedbackFile.uploading = false;
                            clinicalFeedbackFile.progressBarMode = 'determinate';
                            clinicalFeedbackFile.success = true;
                            this.addFileToFormControl(clinicalFeedbackFile.id);
                        }
                    },
                    error: (response: HttpErrorResponse) => this.handleUploadError(clinicalFeedbackFile, response)
                });
                //overall subscriptions to unsubscribe when component it destroyed
                this.subscription.add(uploadSubscription);
                //individual subscription to unsubscribe to upload individually
                clinicalFeedbackFile.subscription = uploadSubscription;
            }
        }
    }

    private handleUploadError(clinicalFeedbackFile: ClinicalFeedbackFile, response: HttpErrorResponse) {
        clinicalFeedbackFile.uploading = false;
        clinicalFeedbackFile.progressBarColor = 'warn';
        clinicalFeedbackFile.progressBarMode = 'determinate';
        // The API checks the file's content too, so a renamed file can pass the browser check but still be rejected.
        // Any other error, including ones without a body, keeps the generic upload error.
        const errorCode = response?.error?.errorCode;
        if (ClinicalFeedbackConstants.FILE_TYPE_ERROR_CODES.includes(errorCode)) {
            clinicalFeedbackFile.error.fileType = true;
        } else if (ClinicalFeedbackConstants.FILE_CONTENT_ERROR_CODES.includes(errorCode)) {
            clinicalFeedbackFile.error.fileContent = true;
        } else {
            clinicalFeedbackFile.error.onUpload = true;
        }
        this.removeFileFromFormControl(clinicalFeedbackFile.id);
    }

    private addFileToDisplayList(file: File): ClinicalFeedbackFile {
        const guid = uuidv4();
        const currentFile = new ClinicalFeedbackFile({
            id: guid,
            name: file.name,
            size: CommonUtilities.bytesToMegabytes(file.size),
            extension: CommonUtilities.getFileExtension(file.name) ?? 'Unknown'
        });
        this.files.push(currentFile);
        return currentFile;
    }

    addFileToFormControl(key: string) {
        const currentFiles = this.control.value !== null ? this.control.value : [];
        const uploadFile = this.files.find(file => file.id === key);
        currentFiles.push({ id: uploadFile.id, name: uploadFile.name });
        this.control.setValue(currentFiles);
    }

    removeFileFromFormControl(key: string) {
        const currentFiles = this.control.value.filter((file: ClinicalFeedbackFile) => file.id !== key);
        this.control.setValue(currentFiles);
    }

    private validateFile(uploadedFile: File, clinicalFeedbackFile: ClinicalFeedbackFile): boolean {
        if (this.files.filter(file => file.success).length + this.files.filter(file => file.uploading).length > this.maxFileAmount) {
            clinicalFeedbackFile.error.fileAmount = true;
        } else if (!acceptedFiletypes.find(fileType => fileType.key === uploadedFile.type)) {
            clinicalFeedbackFile.error.fileType = true;
        } else if (uploadedFile.size > ClinicalFeedbackConstants.MAX_SINGLE_FILE_UPLOAD_SIZE) {
            clinicalFeedbackFile.error.fileSize = true;
        } else {
            return true;
        }
        clinicalFeedbackFile.uploading = false;
        clinicalFeedbackFile.uploadProgress = 100;
        clinicalFeedbackFile.progressBarColor = 'warn';
        clinicalFeedbackFile.progressBarMode = 'determinate';
        return false;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
